<template>
  <v-card flat>
    <!-- Confirm modal -->
    <ConfirmationModal
      ref="activeToggleConfirmationModal"
      :title="confirmationTitle"
      :subtitle="confirmationSubtitle"
      :message="confirmationMessage"
      confirmColor="error darken-1"
    />

    <v-row align="center" justify="space-between">
      <v-col cols="12" md="9">
        <Header
          :title="`List of Connected Persons`"
          subtitle="Manage list of persons who is related to this person"
        />
      </v-col>

      <v-col cols="12" md="3" class="text-md-right">
        <v-btn @click="openForm()" text outlined class="shadow-md text-capitalize"
          >
          <v-icon left>mdi-plus</v-icon>
          Create</v-btn
        >
      </v-col>
    </v-row>

    <!-- Empty state -->
    <div v-if="!connectedPersons?.length" class="text-center">
      <NoData />
      <p class="mt-5 font-weight-bold">No connected persons found</p>
      <v-btn
        @click="openForm()"
        depressed
        text
        outlined
        color="primary"
        min-height="38"
        class="shadow-md text-capitalize"
        >
        <v-icon>mdi-plus</v-icon>
        Create New</v-btn
      >
    </div>

    <!-- List of items -->
    <div v-else class="mt-5">
      <template v-for="(item, i) in connectedPersons">
        <v-card  :key="`connected-person-${i}`" flat outlined class="mb-2">
          <v-card-text>
            <v-row align="center" justify="space-between">
              <v-col cols="12" md="7">
                <div class="d-flex flex-column">
                  <span class="font-weight-medium">
                    {{
                      `${item.firstName} ${item.lastName}`
                    }}
                    {{ `(${item.relation})` }}
                  </span>

                  <ul style="list-style: none" class="pl-0">
                    <li v-if="item.contactNumber" class="d-flex">
                      <p class="mb-0 text-mute">
                        Contact Number:
                      </p>
                      <a class="ml-2" :href="`tel:${item.contactNumber}`">
                        {{ item.contactNumber }}
                      </a>
                    </li>

                    <li v-else class="text-mute">
                      - No contact number available
                    </li>
                    <li v-if="item.emailAddress">
                      <p
                        class="text-caption mb-0 mt-2 text-mute font-weight-bold"
                      >
                        Email:
                      </p>
                      <a :href="`mailto:${item.emailAddress}`">{{
                        item.emailAddress ?? "Not available"
                      }}</a>
                    </li>
                  </ul>
                  
                </div>
              </v-col>
              <v-col class="text-md-right">
                <v-btn
                  small
                  @click="openForm(item, i)"
                  text
                  outlined
                  class="shadow mr-2 text-capitalize"
                  >Edit</v-btn
                >
                <v-btn
                  @click="deletePersonPopup(item, i)"
                  small
                  depressed
                  color="error lighten-1"
                  class="shadow text-capitalize"
                  >Delete</v-btn
                >
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </template>
    </div>

    <!-- Modal for editing table value -->
    <ModalFormLayout
      @submit="onSubmit"
      @cancel="onClose"
      ref="connectedPersonsModal"
      :options="modalOptions"
    >
      <template #content>
        <ValidationObserver ref="connectedPersonsObserver">
          <v-row class="mt-2" dense>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="First Name"
                rules="required|min:2"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  label="First Name"
                  :error-messages="errors"
                  v-model="connectedPerson.firstName"
                >
                  
                  <template v-slot:label>
                    First Name
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Last Name"
                rules="required|min:2|max:45"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  label="Last Name"
                  :error-messages="errors"
                  v-model="connectedPerson.lastName"
                >
                  
                  <template v-slot:label>
                    Last Name
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Alias"
                rules="max:45"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  label="Alias"
                  :error-messages="errors"
                  v-model="connectedPerson.alias"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Contact"
                rules="required|telephone"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  type="tel"
                  label="Contact"
                  :error-messages="errors"
                  v-model="connectedPerson.contactNumber"
                >
                  
                  <template v-slot:label>
                    Contact Number
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Email"
                rules="email|max:256"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  type="email"
                  outlined
                  label="Email"
                  :error-messages="errors"
                  v-model="connectedPerson.email"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Relation"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-select
                  :items="activeRelationSettings"
                  item-text="type"
                  item-value="type"
                  dense
                  outlined
                  label="Relation"
                  :error-messages="errors"
                  v-model="connectedPerson.relation"
                >
                  
                  <template v-slot:label>
                    Relation 
                    <span class="red--text">*</span>
                  </template>
                </v-select>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Street"
                rules="required|min:3|max:70"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  label="Street"
                  :error-messages="errors"
                  v-model="connectedPerson.street"
                >
                  <template v-slot:label>
                    Street 
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="City"
                rules="required|min:2|max:45"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  dense
                  outlined
                  label="City"
                  :error-messages="errors"
                  v-model="connectedPerson.city"
                >
                  <template v-slot:label>
                    City 
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Country"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <CountryPicker
                  dense
                  outlined
                  label="Country"
                  required
                  :error-messages="errors"
                  v-model="connectedPerson.country"
                ></CountryPicker>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6">
              <ValidationProvider
                name="Province"
                rules="required|min:2"
                events="['blur']"
                v-slot="{ errors }"
              >
                <ProvincePicker
                  dense
                  outlined
                  required
                  label="Province"
                  :error-messages="errors"
                  v-model="connectedPerson.state"
                ></ProvincePicker>
              </ValidationProvider>
            </v-col>
          </v-row>
        </ValidationObserver>
      </template>
    </ModalFormLayout>
  </v-card>
</template>
<script>
import { cloneDeep } from 'lodash';
import PageMixin from '@/mixins/page.mixin';
import { mapActions, mapGetters } from 'vuex';
import CountryPicker from '@/modules/shared/components/pickers/CountryPicker.vue';
import ProvincePicker from '@/modules/shared/components/pickers/ProvincePicker.vue';
import Header from './Header.vue';

export default {
  mixins: [PageMixin],
  components: { CountryPicker, ProvincePicker, Header},

  props: {
    outlined : {
      type: Boolean,
      default: true, 
    },
    
    connectedPersons: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      isEditing: false,
      editIndex: null,
      connectedPerson: {},

      // Confirmation modal options
      confirmationTitle: '',
      confirmationMessage: '',
      confirmationSubtitle: '',

      // Search input var
      search: null,
      // Set the loading state of teh datatable
      loading: false,
      // Modal options
      modalOptions: {
        title: 'Add a connected person',
        subtitle: 'Enter the values and click submit',
        submitButtonText: 'Submit',
        maxWidth: '600px',
        width: '600px',
        color: 'white',
        flat: true
      },

      layoutOptions: {
        hideButton: false,
        buttonText: 'Add',
        hideSearch: true, // hide search button
        showSearch: false,
        title: "Connected Person's Information",
        subtitle:
          'This table displays all the connected person information for this person'
      }
    };
  },
  
  mounted() {
    this.getRelationTypes();
  },
  
  computed: {
    ...mapGetters('relationSettingsModule', ['activeRelationSettings'])
  },
  
  methods: {
    ...mapActions('relationSettingsModule', ['getRelationTypes']),
    
    // Open the form for creating/editing a new source
    openForm(item, index = -1) {
      this.modalOptions.title = 'Create Connected Person';
      this.modalOptions.subtitle =
        'A new connected person record will be created';

      console.log(item)
      this.editIndex = index;
      if (item) {
        this.connectedPerson = cloneDeep(item);
        this.modalOptions.title = `Update person`;
        this.modalOptions.subtitle = `Editing connected person record`;
      }

      this.$refs.connectedPersonsModal.open();
    },

    deletePersonPopup(item, index = -1) {
      this.confirmationTitle = 'Confirm Delete';
      this.confirmationMessage = 'Are you sure you want to delete this record?';

      this.editIndex = index;
      this.$refs.activeToggleConfirmationModal.show(async () => {
        let connectedPersons = structuredClone(this.connectedPersons);
        connectedPersons = connectedPersons.filter((e, i) => i !== this.editIndex);
        this.$emit('update', connectedPersons);
      });
    },

    onClose() {
      this.$refs.connectedPersonsObserver?.reset();
      this.connectedPerson = {};
      this.isEditing = false;
    },

    async onSubmit() {
      const valid = await this.$refs.connectedPersonsObserver.validate();
      if (!valid) return;

      let connectedPersons = structuredClone(this.connectedPersons);
      if (this.editIndex > -1) {
        connectedPersons = connectedPersons.map((e, i) => i == this.editIndex ? this.connectedPerson : e );
      } else {
        connectedPersons.push(this.connectedPerson);
      }

      this.$refs.connectedPersonsModal.close();
      this.onClose();
      this.$emit('update', connectedPersons);
    }
  }
};
</script>
